/**
 * Client portal features the typing center can switch on/off
 * (thaklis-doc 14-portal/PORTAL-EMPLOYEES-AND-FEATURES.md §3).
 *
 * effective(client, feature) = client override (ON/OFF) ?? TC default ?? ON, then the extra gate.
 */

export const PORTAL_FEATURES = [
  { key: 'EMPLOYEES', label: 'Employees & documents', hint: 'See employees, open and upload their documents', thaklisOnly: true },
  { key: 'DOC_EXPIRY', label: 'Document Expiry', hint: 'Report of documents about to expire', thaklisOnly: true },
  { key: 'CREATE_WORK_ORDER', label: 'Create work orders', hint: 'Raise new work orders from the portal' },
  { key: 'TASKS', label: 'Tasks', hint: 'Follow the tasks inside their work orders' },
  { key: 'PAYMENTS', label: 'Payments & wallet', hint: 'Pay invoices, top up and see the wallet' },
  { key: 'SAVED_CARDS', label: 'Saved cards', hint: 'Keep cards for online payments' },
];

export const FEATURE_KEYS = PORTAL_FEATURES.map((f) => f.key);
export const featureLabel = (key) => PORTAL_FEATURES.find((f) => f.key === key)?.label || key;

// Client override values. Absent key = DEFAULT (follow the typing center).
export const OVERRIDE = { DEFAULT: 'DEFAULT', ON: 'ON', OFF: 'OFF' };

/** TC defaults: missing key = on. */
export const defaultOn = (defaults, key) => defaults?.[key] !== false;

/** One feature for one client. */
export const resolveFeature = (overrides, defaults, key) => {
  const o = overrides?.[key];
  if (o === OVERRIDE.ON) return true;
  if (o === OVERRIDE.OFF) return false;
  return defaultOn(defaults, key);
};

/** Every feature for one client → { EMPLOYEES: true, … }. */
export const resolveAll = (overrides, defaults) =>
  Object.fromEntries(FEATURE_KEYS.map((k) => [k, resolveFeature(overrides, defaults, k)]));

/** Overrides from a client row (a map, or the JSON string some rows carry). */
export const overridesFromOrg = (org) => {
  const raw = org?.portalFeatureOverrides ?? org?.portal_feature_overrides ?? {};
  return typeof raw === 'string' ? safeParse(raw) : { ...raw };
};

/** "Default" or "Custom · Employees off, Tasks on" for the Organizations list. */
export const overridesSummary = (overrides, defaults) => {
  const set = FEATURE_KEYS.filter((k) => overrides?.[k] === OVERRIDE.ON || overrides?.[k] === OVERRIDE.OFF);
  if (set.length === 0) return { custom: false, text: 'Default' };
  return {
    custom: true,
    text: set.map((k) => `${featureLabel(k)} ${overrides[k] === OVERRIDE.ON ? 'on' : 'off'}`).join(', '),
    differsFromDefault: set.some((k) => resolveFeature(overrides, defaults, k) !== defaultOn(defaults, k)),
  };
};

function safeParse(s) {
  try { return JSON.parse(s) || {}; } catch (e) { return {}; }
}
